/* eslint-disable react/prop-types */
import { useContext, useState } from 'react';
import styled from 'styled-components';
import H3 from '@base/H3/H3';
import H1 from '@base/H1/H1';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import pushRoute from '@utils/pushRoute';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import analyticsAction, { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { CHECKOUT_STARTED } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import ChevronRight from '@icon/ChevronRight';
import imageHelper from '@utils/imageHelper';
import AppInstallBanner from '@comp/AppInstallBanner/Version1';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import useWindowSize from '@hooks/useWindowSize';
import Head from 'next/head';

const Wrapper = styled.section`
  width: 100%;
  position: relative;
  height: 640px;
  background-image: url('${imageHelper.getImage('goal/boy.svg')}'),
    url('${imageHelper.getImage('goal/girl.svg')}'),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(229, 244, 255, 0.44) 100%
    );
  background-position: bottom 20px left, bottom 20px right, center;
  background-repeat: no-repeat;
  text-align: center;
  padding-top: 136px;
  @media (max-width: 70.95em) {
    height: 452px;
    padding-top: 88px;
    background-size: 160px 260px, 160px 260px, 100%;
    background-position: bottom 0px left, bottom 0px right, center;
  }
  @media (max-width: 47.95em) {
    padding: 56px 16px 0;
    height: 393px;
    text-align: left;
    background: linear-gradient(180deg, #ffffff 0%, #f1f9ff 100%);
  }
`;

const ActiveButton = styled.button`
  margin-left: auto;
  margin-right: auto;
  background: linear-gradient(95.84deg, #2d81f7 0%, #08bd80 100%);
  display: flex;
  align-items: center;
  padding: 0px;
  padding-right: 8px;
  border: none;
  border-radius: 4px;
  @media (max-width: 70.95em) {
    display: none;
  }
`;

const BtnText = styled(P2)`
  color: #fff;
  padding: 6px 6px 6px 12px;
  font-weight: 600;
  font-size: 12px;
  line-height: 150% !important;
`;

const Header = styled.div`
  margin: 24px auto 32px auto;
  @media (max-width: 70.95em) {
    margin-bottom: 16px;
  }
  @media (max-width: 47.95em) {
    margin: 0;
    text-align: left;
  }
`;

const SubHeaderContainer = styled.div`
  margin: 0px auto 40px auto;
  width: 700px;
  @media (max-width: 70.95em) {
    width: 480px;
    margin-bottom: 32px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin: 12px 0 24px;
    padding-right: 16px;
  }
`;

const SubHeader = styled(H3)`
  font-weight: normal;
  line-height: 150%;
  @media (max-width: 70.95em) {
    font-size: 16px;
  }
  @media (max-width: 47.95em) {
    font-size: 14px;
  }
`;

const SubButton = styled(Button)`
  margin: auto;
  width: 217px;
  height: 60px;
  font-size: 20px;
  display: inline-block;
  @media (max-width: 70.95em) {
    min-height: 48px;
    height: 48px;
    font-size: 16px;
    width: 171px;
  }
  @media (max-width: 47.95em) {
    width: 160px;
    margin: 0;
    display: inline-block;
  }
`;

const Image = styled.img`
  display: none;
  @media (max-width: 47.95em) {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 180px;
    height: 220px;
    display: block;
  }
`;

const LineBreak = styled.br`
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Title = styled(H1)`
  @media (max-width: 70.95em) {
    font-size: 32px;
    line-height: 120%;
  }
`;

const PageHead = ({ block, goalUID, goalSlug, goalDetails, isSubscribed }) => {
  const purchaseType = useContext(PurchaseTypeContext);
  const { name, isNoTestGoal } = goalDetails;
  const [openDrawer, setOpenDrawer] = useState(false);
  const dispatch = useDispatchToStore();
  const { isMobile } = useWindowSize();
  let CTALabel = usePurchaseCTAHandler(goalUID);
  if (isSubscribed) {
    CTALabel = 'Download App';
  }
  const handleSubscriptionClick = () => {
    if (isSubscribed) {
      setOpenDrawer(true);
      return;
    }
    dispatch(analyticsAction('goal-getSubscription', goalUID, goalDetails, {}));
    segment.track(
      CHECKOUT_STARTED,
      {
        purchase_type: purchaseType,
        last_primary_source: segmentStorage.getItem('lps')
      },
      evtNameCategoryMap.BOTH
    );
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`,
      {},
      'goal'
    );
  };

  const closeDrawer = () => {
    setOpenDrawer(false);
  };

  let title = `Crack ${name} with`;
  let subtitle = "India's largest learning platform";
  let subheader =
    "Get Plus subscription and access unlimited live and recorded courses from India's best educators";

  if (goalUID === 'QZIXN') {
    subtitle = "Brazil's Top Educators";
    subheader =
      "Get Plus subscription and access unlimited live and recorded courses from Brazil's best educators";
  }

  if (isNoTestGoal && block?.data[0]) {
    title = block.data[0].name;
    subtitle = block.data[0].tag_line;
    subheader = block.data[0].description;
  }

  return (
    <>
      {isMobile ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={imageHelper.getImage('goal/goal-bg.png')}
          />
        </Head>
      ) : null}
      <Wrapper>
        <ActiveButton>
          <a
            href="https://www.youtube.com/watch?v=hz5wsiXWeeo"
            target="_blank"
            rel="noopener noreferrer"
          >
            <BtnText>LISTEN TO OUR NEW ANTHEM</BtnText>
          </a>
          <ChevronRight size="16px" color="#fff" />
        </ActiveButton>
        <Header>
          <Title>
            {`${title} `}
            <LineBreak />
            {subtitle}
          </Title>
        </Header>
        <SubHeaderContainer>
          <SubHeader>{subheader}</SubHeader>
        </SubHeaderContainer>
        <SubButton
          label={CTALabel}
          onClick={handleSubscriptionClick}
          dataAnalytics="goal-getSubscription"
        />
        {isMobile ? (
          <Image
            src={imageHelper.getImage('goal/goal-bg.png')}
            alt="Learning"
            width="150"
            height="220"
          />
        ) : null}
      </Wrapper>
      <AppInstallBanner onClose={closeDrawer} open={openDrawer} />
    </>
  );
};

export default PageHead;
